<template>
  <div class="relationships-tab">
    <div v-if="availability === 'unavailable'" class="relationship-gate">
      <div class="relationship-gate__icon"><v-icon size="28">mdi-api-off</v-icon></div>
      <div class="relationship-gate__body">
        <h3>Relationships are not available here</h3>
        <p>This deployment does not offer relationship tools yet. Your register data is unchanged.</p>
        <v-btn small outlined color="primary" :loading="loading" @click="load">
          <v-icon left small>mdi-refresh</v-icon>Check again
        </v-btn>
      </div>
    </div>

    <div v-else-if="availability === 'forbidden'" class="relationship-gate">
      <div class="relationship-gate__icon"><v-icon size="28">mdi-lock-outline</v-icon></div>
      <div class="relationship-gate__body">
        <h3>You do not have access to relationships for this register</h3>
        <p>Ask a register administrator for access. Your register data is unchanged.</p>
        <p v-if="problemDetail" class="relationship-gate__detail">{{ problemDetail }}</p>
      </div>
    </div>

    <div v-else-if="availability === 'error'" class="relationship-gate" role="alert">
      <div class="relationship-gate__icon"><v-icon size="28" color="error">mdi-alert-circle-outline</v-icon></div>
      <div class="relationship-gate__body">
        <h3>Relationships could not be loaded</h3>
        <p>The server did not respond. Check your connection, then try again. Nothing was changed.</p>
        <p v-if="problemDetail" class="relationship-gate__detail">{{ problemDetail }}</p>
        <v-btn small color="primary" depressed :loading="loading" @click="load">Try again</v-btn>
      </div>
    </div>

    <div v-else-if="!scope.kind" class="relationship-gate">
      <div class="relationship-gate__icon"><v-icon size="28">mdi-map-marker-off-outline</v-icon></div>
      <div class="relationship-gate__body">
        <h3>{{ scope.invalid ? 'This register has conflicting owners' : 'This register has no ownership scope' }}</h3>
        <p v-if="scope.invalid">A register cannot belong to a Facility and a Facility Group at the same time. Repair the ownership conflict before declaring a relationship.</p>
        <p v-else>A relationship must belong to exactly one Facility or Facility Group. Add a register owner before declaring it.</p>
      </div>
    </div>

    <div
      v-else-if="!initialLoading
        && !scopedCapabilities.length
        && !relationships.length
        && historyAvailability === 'ready'
        && !runHistory.length"
      class="relationship-gate">
      <div class="relationship-gate__icon"><v-icon size="28">mdi-puzzle-outline</v-icon></div>
      <div class="relationship-gate__body">
        <h3>New relationships are not available for this register</h3>
        <p>This register has no related record types available yet. No changes were made.</p>
      </div>
    </div>

    <div v-else class="relationship-workspace">
      <main class="relationship-workspace__pane">
        <header class="relationship-workspace__pane-header">
          <div class="relationship-workspace__pane-header-inner">
            <div class="relationship-workspace__pane-title">
              <h2>{{ activeWorkspaceMeta.title }}</h2>
              <p>{{ activeWorkspaceMeta.description }}</p>
            </div>
            <div class="relationship-workspace__pane-actions">
              <span
                v-if="currentWorkspaceView === 'history' && historyLoadedLabel"
                class="relationship-workspace__loaded-count">
                {{ historyLoadedLabel }}
              </span>
              <v-btn icon :loading="loading" aria-label="Refresh relationship workspace" @click="load">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
              <v-tooltip bottom :disabled="canDeclare">
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    <v-btn color="primary" depressed :disabled="!canDeclare" @click="openCreate">
                      <v-icon left>mdi-plus</v-icon>New relationship
                    </v-btn>
                  </span>
                </template>
                <span>{{ declareDisabledReason }}</span>
              </v-tooltip>
            </div>
          </div>
        </header>

        <div class="relationship-workspace__body">
          <div
            v-if="closedRunMonitorExhausted"
            class="relationship-readonly-notice relationship-run-monitor-notice"
            role="alert"
            aria-live="polite">
            <v-icon color="warning">mdi-cloud-refresh-outline</v-icon>
            <div>
              <strong>Run status needs to be checked</strong>
              <span>Automatic checking paused after 20 minutes. Check again to see whether the run finished.</span>
            </div>
            <v-btn small outlined color="primary" @click="retryClosedRunMonitoring">
              <v-icon left small>mdi-refresh</v-icon>Check status
            </v-btn>
          </div>

          <div
            v-if="initialLoading && currentWorkspaceView === 'declarations'"
            class="relationship-skeleton"
            role="status"
            aria-live="polite"
            aria-busy="true">
            <span class="ds-sr-only">Loading relationships</span>
            <div class="relationship-overview relationship-skeleton__overview" aria-hidden="true">
              <div v-for="cell in 4" :key="`overview-${cell}`" class="relationship-overview__stat">
                <span class="relationship-skeleton__bone relationship-skeleton__bone--label"></span>
                <span class="relationship-skeleton__bone relationship-skeleton__bone--value"></span>
                <span class="relationship-skeleton__bone relationship-skeleton__bone--detail"></span>
              </div>
            </div>
            <section class="relationship-declarations" aria-hidden="true">
              <article v-for="card in 2" :key="`card-${card}`" class="relationship-card">
                <div class="relationship-card__header">
                  <div class="relationship-card__identity">
                    <span class="relationship-skeleton__bone relationship-skeleton__bone--dot"></span>
                    <div class="relationship-skeleton__stack">
                      <span class="relationship-skeleton__bone relationship-skeleton__bone--title"></span>
                      <span class="relationship-skeleton__bone relationship-skeleton__bone--caption"></span>
                    </div>
                  </div>
                  <div class="relationship-skeleton__actions">
                    <span class="relationship-skeleton__bone relationship-skeleton__bone--action"></span>
                    <span class="relationship-skeleton__bone relationship-skeleton__bone--kebab"></span>
                  </div>
                </div>
                <div class="relationship-skeleton__rail">
                  <span
                    v-for="token in 5"
                    :key="`token-${token}`"
                    class="relationship-skeleton__bone"
                    :class="token % 2 ? 'relationship-skeleton__bone--token' : 'relationship-skeleton__bone--word'"></span>
                </div>
                <div class="relationship-card__footer">
                  <span class="relationship-skeleton__bone relationship-skeleton__bone--chip"></span>
                  <span class="relationship-skeleton__bone relationship-skeleton__bone--meta"></span>
                </div>
              </article>
            </section>
          </div>

          <template v-else-if="currentWorkspaceView === 'declarations'">
            <div class="relationship-overview" aria-label="Relationship operational summary">
              <div v-for="cell in overviewCells" :key="cell.key" class="relationship-overview__stat">
                <span>{{ cell.label }}</span>
                <strong :class="{ 'relationship-overview__value--text': cell.isText }">{{ cell.value }}</strong>
                <small v-if="cell.detail">{{ cell.detail }}</small>
              </div>
            </div>

            <p class="relationship-scope-note">
              <v-icon color="primary" size="16">mdi-format-list-checks</v-icon>
              <span>{{ selectionSummary.title }} - {{ selectionSummary.detail }}</span>
            </p>

            <section v-if="!relationships.length" class="relationship-declarations relationship-declarations--empty">
              <div class="relationship-empty__intro">
                <div class="relationship-empty__mark" aria-hidden="true">
                  <v-icon color="primary" size="26">mdi-vector-link</v-icon>
                </div>
                <div>
                  <h3>Create the first relationship</h3>
                  <p>Connect register rows to the records they belong to, then review the setup before running it.</p>
                </div>
                <v-btn color="primary" depressed :disabled="!canDeclare" @click="openCreate">
                  <v-icon left>mdi-plus</v-icon>Create relationship
                </v-btn>
              </div>
              <ol class="relationship-empty__steps" aria-label="Relationship workflow">
                <li>
                  <span>1</span>
                  <div><strong>Choose a register field</strong><small>Select the field that identifies each record.</small></div>
                </li>
                <li>
                  <span>2</span>
                  <div><strong>Choose the related record</strong><small>Select the record type and matching field.</small></div>
                </li>
                <li>
                  <span>3</span>
                  <div><strong>Review and save</strong><small>Check the relationship setup before creating it.</small></div>
                </li>
              </ol>
            </section>

            <section v-else class="relationship-declarations" aria-label="Relationships">
              <article
          v-for="relationship in relationships"
          :key="relationship.id"
          class="relationship-card"
          :class="{ 'relationship-card--quarantine': relationship.attributionStatus === 'quarantine' }">
          <div class="relationship-card__header">
            <div class="relationship-card__identity">
              <div
                class="relationship-card__status-dot"
                :class="relationshipStatusClass(relationship)"
                aria-hidden="true"></div>
              <div>
                <div class="d-flex align-center flex-wrap">
                  <h3>{{ relationship.name }}</h3>
                  <v-chip v-if="relationship.attributionStatus === 'quarantine'" x-small outlined color="warning" class="ml-2">Needs review</v-chip>
                </div>
                    <span>{{ relationship.isEnabled ? 'Enabled' : 'Disabled' }}</span>
              </div>
            </div>
            <div class="relationship-card__actions">
              <v-menu offset-y left content-class="relationship-card-menu">
                <template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
                  <v-btn icon class="ml-1" v-bind="menuAttrs" v-on="menuOn" :aria-label="`Actions for ${relationship.name}`">
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item
                    :disabled="!canRun(relationship)"
                    @click="openResolveRun(relationship)">
                    <v-list-item-icon><v-icon small>mdi-link-variant-plus</v-icon></v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>Connect records</v-list-item-title>
                      <v-list-item-subtitle>{{ resolvePreviewDescription(relationship) }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item
                    :disabled="!canSync(relationship)"
                    @click="openSyncRun(relationship)">
                    <v-list-item-icon><v-icon small>mdi-table-sync</v-icon></v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>Sync changes</v-list-item-title>
                      <v-list-item-subtitle>Update mapped fields on existing linked records</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item
                    :disabled="Boolean(refreshingStatusRelationshipId) || loading || loadingRunHistory"
                    :aria-label="`Refresh status for ${relationship.name}`"
                    @click="refreshRelationshipStatus(relationship)">
                    <v-list-item-icon>
                      <v-progress-circular
                        v-if="isRefreshingStatus(relationship)"
                        indeterminate
                        size="18"
                        width="2"
                        color="primary"
                        aria-label="Refreshing status"></v-progress-circular>
                      <v-icon v-else small>mdi-refresh</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ isRefreshingStatus(relationship) ? 'Refreshing status...' : 'Refresh status' }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item
                    :disabled="!canEditRelationship || relationship.isEnabled"
                    @click="openEdit(relationship)">
                    <v-list-item-icon><v-icon small>mdi-pencil-outline</v-icon></v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>Edit relationship</v-list-item-title>
                      <v-list-item-subtitle v-if="relationship.isEnabled">
                        Disable the relationship before editing
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item
                    :disabled="!canValidateRelationship(relationship) || isValidating(relationship)"
                    :aria-label="`Validate ${relationship.name}`"
                    @click="validate(relationship)">
                    <v-list-item-icon>
                      <v-progress-circular
                        v-if="isValidating(relationship)"
                        indeterminate
                        size="18"
                        width="2"
                        color="primary"
                        aria-label="Validation in progress"></v-progress-circular>
                      <v-icon v-else small>mdi-check-decagram-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ isValidating(relationship) ? 'Validating...' : 'Validate' }}</v-list-item-title>
                      <v-list-item-subtitle v-if="!canValidateRelationship(relationship)">
                        {{ validationDisabledReason }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item :disabled="!canToggleEnabled(relationship)" @click="toggleEnabled(relationship)">
                    <v-list-item-icon><v-icon small>{{ relationship.isEnabled ? 'mdi-pause-circle-outline' : 'mdi-play-circle-outline' }}</v-icon></v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ relationship.isEnabled ? 'Disable' : 'Enable' }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider></v-divider>
                  <v-list-item :disabled="!canDeleteRelationship(relationship)" @click="openDelete(relationship)">
                    <v-list-item-icon><v-icon small color="error">mdi-delete-outline</v-icon></v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="error--text">Delete relationship</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </div>

          <relationship-path-rail
            class="relationship-card__rail"
            dense
            :source="sourceLabel(relationship)"
            :target="targetLabel(relationship)"
            :match-field="relationship.matchField || ''"
            :resolution-mode="relationship.onMiss === 'create' ? 'Resolve existing or create after approval' : 'Resolve existing only'"
            :projections="relationshipProjections(relationship)" />

          <div class="relationship-card__footer">
            <div class="relationship-card__health">
              <template v-if="lastRunSummaryFor(relationship)">
                <span v-if="lastRunSummaryFor(relationship).statusLabel" class="relationship-card__run-status">
                  <v-icon small :color="lastRunSummaryFor(relationship).statusColor">
                    {{ lastRunSummaryFor(relationship).statusIcon }}
                  </v-icon>
                  {{ lastRunSummaryFor(relationship).statusLabel }}
                </span>
                <v-chip
                  v-for="entry in lastRunSummaryFor(relationship).counts"
                  :key="entry.key"
                  x-small
                  outlined
                  :color="entry.color">
                  {{ entry.count }} {{ entry.label }}
                </v-chip>
              </template>
              <v-chip
                v-for="entry in healthEntries(relationship).slice(0, 4)"
                :key="entry.key"
                x-small
                outlined
                :color="entry.color">
                {{ entry.count }} {{ entry.label }}
              </v-chip>
              <span v-if="healthEntries(relationship).length > 4" class="text-caption">+{{ healthEntries(relationship).length - 4 }} more</span>
              <span v-if="notRunNotice(relationship)" class="text-caption">{{ notRunNotice(relationship) }}</span>
            </div>
            <div v-if="lastRunSummaryFor(relationship)" class="relationship-card__health-meta">
              <span v-if="lastRunSummaryFor(relationship).at">
                Last run {{ formatDate(lastRunSummaryFor(relationship).at) }}
              </span>
              <span v-if="coverageLabel(relationship)">{{ coverageLabel(relationship) }}</span>
            </div>
          </div>
          <div v-if="needsAttention(relationship)" class="relationship-card__recovery" role="status">
            <v-icon small color="warning">mdi-alert-circle-outline</v-icon>
            <div>
              <strong>This relationship needs review</strong>
              <span>Open the latest run to see affected rows and available actions.</span>
            </div>
            <v-btn
              v-if="latestRunFor(relationship)"
              small
              outlined
              color="warning"
              @click="openHistoryRun(latestRunFor(relationship))">
              Review latest run
            </v-btn>
          </div>
          <div
            v-if="isValidating(relationship)"
            class="relationship-card__validation relationship-card__validation--loading"
            role="status"
            aria-live="polite">
            <v-progress-circular indeterminate size="18" width="2" color="primary"></v-progress-circular>
                  <div><strong>Checking this relationship...</strong></div>
          </div>
          <div
            v-else-if="validationFeedbackFor(relationship)"
            class="relationship-card__validation"
            :class="`relationship-card__validation--${validationFeedbackFor(relationship).type}`"
            :role="validationFeedbackFor(relationship).type === 'error' ? 'alert' : 'status'"
            aria-live="polite">
            <v-icon small :color="validationFeedbackFor(relationship).type === 'error' ? 'error' : 'success'">
              {{ validationFeedbackFor(relationship).type === 'error' ? 'mdi-alert-circle-outline' : 'mdi-check-decagram-outline' }}
            </v-icon>
            <div>
              <strong>{{ validationFeedbackFor(relationship).message }}</strong>
            </div>
          </div>
              </article>
              <v-btn
                v-if="relationshipsCursor"
                block
                text
                color="primary"
                class="relationship-declarations__load-more"
                :loading="loadingMoreRelationships"
                @click="loadMoreRelationships">
                <v-icon left small>mdi-chevron-down</v-icon>Load more relationships
              </v-btn>
            </section>
          </template>

          <section
            v-else-if="historyAvailability === 'ready' && !runHistory.length"
            class="relationship-history-empty"
            aria-labelledby="relationship-history-empty-title">
            <div class="relationship-history-empty__mark" aria-hidden="true">
              <v-icon color="primary" size="28">mdi-history</v-icon>
            </div>
            <div>
              <h3 id="relationship-history-empty-title">No run history yet</h3>
              <p>Previews and completed runs will appear here.</p>
            </div>
          </section>

          <relationship-run-history-panel
            v-else
            class="relationship-workspace__history"
            :runs="runHistory"
            :availability="historyAvailability"
            :cursor="runHistoryCursor"
            :total="runHistoryTotal"
            :loading="loadingRunHistory"
            :problem="historyProblem"
            @open="openHistoryRun"
            @retry="retryHistory"
            @load-more="loadMoreHistory" />

        </div>
      </main>
    </div>

    <relationship-run-history-panel
      v-if="showStandaloneHistory"
      class="relationships-tab__standalone-history mt-4"
      :runs="runHistory"
      :availability="historyAvailability"
      :cursor="runHistoryCursor"
      :total="runHistoryTotal"
      :loading="loadingRunHistory"
      :problem="historyProblem"
      @open="openHistoryRun"
      @retry="retryHistory"
      @load-more="loadMoreHistory" />

    <relationship-editor-dialog
      v-if="editorOpen"
      v-model="editorOpen"
      :register="register"
      :relationship="editingRelationship"
      :capabilities="scopedCapabilities"
      :can-manage-schema="access.canManageSchema === true"
      :saving="saving"
      :problem="problem"
      @save="saveRelationship" />

    <relationship-run-dialog
      v-if="runDialogOpen && runningRelationship"
      v-model="runDialogOpen"
      :relationship="runningRelationship"
      :selected-row-ids="selectedRowIds"
      :data-filter-snapshot="dataFilterSnapshot"
      :reconcile="false"
      :preview-mode="runPreviewMode"
      :confirm-allowed="access.canConfirm === true"
      :cancel-allowed="access.canPreview === true"
      :target-scope-kind="scope.kind"
      :owner-scope="scope"
      :initial-run-id="historyRunId"
      :history-only="historyMode"
      @run-replaced="onRunReplaced"
      @run-terminal="onRunTerminal"
      @show-attention-rows="$emit('show-attention-rows', $event)" />

    <v-dialog v-model="deleteDialogOpen" max-width="560" persistent>
      <v-card class="ds-dialog">
        <header class="ds-dialog__header">
          <div class="ds-dialog__header-icon"><v-icon color="error">mdi-delete-alert-outline</v-icon></div>
          <div class="ds-dialog__header-text">
            <span class="ds-dialog__title">Delete relationship</span>
            <span class="ds-dialog__subtitle">The target records survive; this relationship's active links are removed.</span>
          </div>
        </header>
        <v-card-text class="ds-dialog__body">
          <p>Delete <strong>{{ deletingRelationship && deletingRelationship.name }}</strong>?</p>
          <div class="ds-info-box ds-info-box--warning mb-4">
            <v-icon color="warning">mdi-history</v-icon>
            <div>Previous run history remains available after the relationship is deleted.</div>
          </div>
          <v-checkbox v-model="deleteReviewed" hide-details label="I understand this removes the relationship associations, not the targets."></v-checkbox>
        </v-card-text>
        <footer class="ds-dialog__footer">
          <v-btn text :disabled="saving" @click="deleteDialogOpen = false">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="error" depressed :loading="saving" :disabled="!deleteReviewed || !canDeleteRelationship(deletingRelationship)" @click="confirmDelete">Delete relationship</v-btn>
        </footer>
      </v-card>
    </v-dialog>

    <!-- Raised only by the server's refusal to enable a relationship saved before automatic sync.
         Enabling it rewrites the saved mapping, so the workspace asks first and upgrades second. -->
    <v-dialog v-model="upgradeDialogOpen" max-width="560" persistent>
      <v-card class="ds-dialog">
        <header class="ds-dialog__header">
          <div class="ds-dialog__header-icon"><v-icon color="primary">mdi-sync</v-icon></div>
          <div class="ds-dialog__header-text">
            <span class="ds-dialog__title">Upgrade to automatic sync</span>
            <span class="ds-dialog__subtitle">Enabling this relationship rewrites its saved mapping.</span>
          </div>
        </header>
        <v-card-text class="ds-dialog__body">
          <p><strong>{{ upgradingRelationship && upgradingRelationship.name }}</strong> was saved before automatic sync, so it cannot be enabled as it stands.</p>
          <div class="ds-info-box ds-info-box--info mb-4" role="note">
            <v-icon color="primary">mdi-swap-horizontal</v-icon>
            <div>
              <strong>Every mapped field syncs in both directions</strong>
              <span>An existing record is linked without overwriting either side; later changes flow automatically between the register and the related record.</span>
            </div>
          </div>
          <p class="mb-0">The upgrade changes this relationship's configuration only. Records change when it next runs or syncs.</p>
        </v-card-text>
        <footer class="ds-dialog__footer">
          <v-btn text :disabled="saving" @click="closeAutomaticUpgrade">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" depressed :loading="saving" @click="confirmAutomaticUpgrade">Upgrade and enable</v-btn>
        </footer>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import RelationshipEditorDialog from './RelationshipEditorDialog.vue';
import RelationshipPathRail from './RelationshipPathRail.vue';
import RelationshipRunHistoryPanel from './RelationshipRunHistoryPanel.vue';
import RelationshipRunDialog from './RelationshipRunDialog.vue';
import { RELATIONSHIP_PREVIEW_MODES } from '../../services/registerRelationshipService';
import { RELATIONSHIP_AUTOMATIC_CONTRACT_UPGRADE_REQUIRED } from '../../store/relationships';
import { log as debugLog } from '@/utils/debugLogger';
import {
  capabilitySupportsScope,
  formatRelationshipTimestamp,
  latestRelationshipRun,
  registerColumnLabel,
  registerOwnershipScope,
  relationshipAttentionSummary,
  relationshipCoverageLabel,
  relationshipHealthEntries,
  relationshipNeedsAttention,
  relationshipRunCountEntries,
  RELATIONSHIP_RUN_TERMINAL_STATUSES,
  relationshipSourceLabel,
  relationshipTargetLabel,
  runStatusMeta,
  DESTINATION_NAMESPACE_LABELS,
} from '../../config/relationshipUi';
import { toastCopy, toastHtml } from '../../config/relationshipRefusalCopy';

const CLOSED_RUN_POLL_INTERVAL_MS = 2500;
// The API's conservative execution window is 17 minutes; keep three minutes of client buffer.
const CLOSED_RUN_MONITOR_WINDOW_MS = 20 * 60 * 1000;
const CLOSED_RUN_STATUS_REQUEST_TIMEOUT_MS = 15 * 1000;

export default {
  name: 'RelationshipsTab',
  components: {
    RelationshipEditorDialog,
    RelationshipPathRail,
    RelationshipRunDialog,
    RelationshipRunHistoryPanel,
  },
  props: {
    register: { type: Object, required: true },
    selectedRowIds: { type: Array, default: () => [] },
    dataFilterSnapshot: { type: Object, default: () => ({}) },
    activeView: { type: String, default: 'relationships' },
  },
  data() {
    return {
      editorOpen: false,
      editingRelationship: null,
      runDialogOpen: false,
      runningRelationship: null,
      historyRunId: null,
      historyMode: false,
      runPreviewMode: RELATIONSHIP_PREVIEW_MODES.RESOLVE,
      deleteDialogOpen: false,
      deletingRelationship: null,
      deleteReviewed: false,
      upgradeDialogOpen: false,
      // Held by id, not by object: the workspace refreshes between the server's refusal and the
      // user's answer, and a detached copy would carry a spent ETag into the retry.
      upgradingRelationshipId: null,
      validationFeedbackById: {},
      refreshingStatusRelationshipId: null,
      closedRunPoller: null,
      closedRunAbortController: null,
      closedRunPollInFlight: false,
      closedRunPollGeneration: 0,
      closedRunMonitorStartedAt: null,
      closedRunMonitorExhausted: false,
      closedRunContext: null,
      closedRunRefreshPending: false,
      terminalRunIds: [],
    };
  },
  computed: {
    ...mapState('REGISTERS/relationships', [
      'availability',
      'registerSchema',
      'capabilities',
      'relationships',
      'relationshipsCursor',
      'relationshipsTotal',
      'loadingMoreRelationships',
      'validatingRelationshipIds',
      'access',
      'runHistory',
      'historyAvailability',
      'historyProblem',
      'runHistoryCursor',
      'runHistoryTotal',
      'loadingRunHistory',
      'loading',
      'saving',
      'problem',
      'currentRun',
      'workspaceRequestId',
    ]),
    scope() { return registerOwnershipScope(this.register); },
    refusalCopyContext() {
      const label = typeof this.scope.label === 'string' ? this.scope.label.trim() : '';
      return {
        groupName: this.scope.kind === 'facilityGroup' && label !== 'Facility Group' ? label : '',
        registerKind: this.scope.kind,
      };
    },
    registerIdentity() {
      const facilityId = this.register.facilityId || this.register.facility?.id || '';
      const groupId = this.register.facilityGroupId || this.register.facilityGroup?.id || '';
      return `${this.register.id || ''}:${facilityId}:${groupId}`;
    },
    currentWorkspaceView() {
      return this.activeView === 'history' ? 'history' : 'declarations';
    },
    activeWorkspaceMeta() {
      return {
        declarations: {
          title: 'Relationships',
          description: 'Create and manage how register rows connect to related records.',
        },
        history: {
          title: 'Run history',
          description: 'Review previous previews and completed runs.',
        },
      }[this.currentWorkspaceView];
    },
    // A workspace that has never answered has no count to report; the loaded content is what
    // replaces the skeleton, so nothing in the chrome should assert a number before then.
    initialLoading() { return this.loading && this.availability === 'unknown'; },
    historyLoadedLabel() {
      if (this.historyAvailability !== 'ready') return '';
      const total = this.runHistoryTotal === null ? this.runHistory.length : this.runHistoryTotal;
      return `${total} ${total === 1 ? 'run' : 'runs'}`;
    },
    canEditRelationship() { return this.access.canEdit === true; },
    // Re-resolved from the live list every time it is read, so the confirmation always names — and
    // the retry always sends — the relationship as the workspace currently holds it.
    upgradingRelationship() {
      if (!this.upgradingRelationshipId) return null;
      return this.relationships.find((item) => item.id === this.upgradingRelationshipId) || null;
    },
    validationDisabledReason() {
      if (this.availability !== 'ready') return 'Relationship workspace is unavailable.';
      if (this.access.canEdit !== true) return 'Register administrator permission is required.';
      return '';
    },
    scopedCapabilities() {
      return this.capabilities.filter((capability) => capabilitySupportsScope(capability, this.scope.kind));
    },
    // The store fills problem.detail with a generic fallback that repeats the gate heading. Only a
    // server-supplied detail adds anything, so a restatement of the heading is dropped.
    problemDetail() {
      const detail = (this.problem?.detail || '').trim();
      if (!detail) return '';
      const normalise = (text) => text.toLowerCase().replace(/[.\s]+$/, '');
      return normalise(detail) === normalise('Relationships could not be loaded') ? '' : detail;
    },
    canDeclare() {
      return this.availability === 'ready'
        && this.scope.kind
        && this.scopedCapabilities.length > 0
        && this.access.canDeclare === true;
    },
    declareDisabledReason() {
      if (this.initialLoading) return 'Relationships are still loading.';
      if (this.availability !== 'ready') return 'Relationships are not available right now.';
      if (!this.scope.kind) return 'The register has no Facility or Facility Group owner.';
      if (!this.scopedCapabilities.length) return 'No related record types are available for this register.';
      if (!this.access.canDeclare) return 'Register administrator permission is required.';
      return '';
    },
    enabledCount() { return this.relationships.filter((item) => item.isEnabled).length; },
    showStandaloneHistory() {
      // The workspace already renders its own loading state. A second history panel loading
      // underneath it would be a competing indicator for the same wait.
      if (this.initialLoading) return false;
      const coreGate = ['unavailable', 'error'].includes(this.availability) || !this.scope.kind;
      return coreGate && (this.runHistory.length > 0 || this.historyAvailability !== 'ready');
    },
    // Run-derived cells cannot be stated until run history has settled. An errored history is
    // settled: it will not arrive, so the cell says so rather than withholding a number forever.
    runHistorySettled() { return !['unknown', 'loading'].includes(this.historyAvailability); },
    awaitingConfirmationRuns() {
      return this.runHistory.filter((run) => run.status === 'awaitingConfirmation');
    },
    relationshipsCell() {
      if (this.availability !== 'ready') return { value: '—', detail: '', isText: true };
      const total = Number.isInteger(this.relationshipsTotal)
        ? this.relationshipsTotal
        : this.relationships.length;
      if (!total) return { value: 'None yet', detail: '', isText: true };
      // Enabled can only be counted over loaded relationships, so it is only stated once the list
      // is complete. Otherwise the cell reports what it does know: how much of the list is loaded.
      if (this.relationshipsCursor) {
        return { value: total, detail: `${this.relationships.length} loaded so far`, isText: false };
      }
      const enabled = this.enabledCount;
      const detail = enabled === total
        ? 'All enabled'
        : `${enabled || 'None'} enabled`;
      return { value: total, detail, isText: false };
    },
    awaitingConfirmationCell() {
      if (!this.runHistorySettled) return { value: '—', detail: '', isText: true };
      const runs = this.awaitingConfirmationRuns;
      if (!runs.length) return { value: 0, detail: 'Nothing to approve', isText: false };
      const nearestExpiry = runs
        .map((run) => run.expiresAt)
        .filter(Boolean)
        .reduce(
          (earliest, value) => (!earliest || new Date(value) < new Date(earliest) ? value : earliest),
          null,
        );
      return {
        value: runs.length,
        detail: nearestExpiry ? `First expires ${formatRelationshipTimestamp(nearestExpiry)}` : '',
        isText: false,
      };
    },
    // Counts RELATIONSHIPS, not items: this cell sits directly above the cards, so its number has
    // to match the cards a user can scan for. It uses the same predicate as each card's review
    // block, so no state can exist where a card is flagged and this cell reads zero.
    relationshipsNeedingReview() {
      return this.relationships.filter((item) => relationshipNeedsAttention(item, this.runHistory));
    },
    reviewCell() {
      if (!this.runHistorySettled) return { value: '—', detail: '', isText: true };
      const flagged = this.relationshipsNeedingReview;
      if (!flagged.length) return { value: 0, detail: '', isText: false };
      // Item granularity already lives on each card's chips; here it is only an annotation, and
      // only when the flagged relationships actually have counted items behind them. The detail
      // names both units and how many last runs the item number speaks for, because a flagged
      // relationship can have no loaded executed run at all.
      const { items, covered } = relationshipAttentionSummary(flagged, this.runHistory);
      const detail = items
        ? `${items} ${items === 1 ? 'item' : 'items'} from the last ${covered === 1 ? 'run' : `${covered} runs`}`
        : '';
      return { value: flagged.length, detail, isText: false };
    },
    lastActivityCell() {
      if (!this.runHistorySettled) return { value: '—', detail: '', isText: true };
      const run = this.runHistory[0];
      if (!run) return { value: 'No runs yet', detail: '', isText: true };
      return {
        value: runStatusMeta(run.status).label,
        detail: formatRelationshipTimestamp(run.completedAt || run.createdAt),
        isText: true,
      };
    },
    overviewCells() {
      return [
        { key: 'relationships', label: 'Relationships', ...this.relationshipsCell },
        { key: 'awaiting', label: 'Awaiting confirmation', ...this.awaitingConfirmationCell },
        { key: 'review', label: 'Needs review', ...this.reviewCell },
        { key: 'activity', label: 'Last activity', ...this.lastActivityCell },
      ];
    },
    selectionSummary() {
      const count = this.selectedRowIds.length;
      // A run with no selection is scoped to the whole register - see RelationshipRunDialog.
      if (!count) return { title: 'No rows selected', detail: 'Runs cover the whole register' };
      return {
        title: `${count} ${count === 1 ? 'row' : 'rows'} selected in the Data tab`,
        detail: 'Runs can be limited to this selection',
      };
    },
    // The eleven health buckets always arrive as zero, so "no runs completed" was never a fact about
    // this relationship. The run history is the only client-side source that knows what a run did.
    lastRunSummaryById() {
      return Object.fromEntries(this.relationships.map((relationship) => {
        const run = this.latestRunFor(relationship);
        if (run) {
          const meta = runStatusMeta(run.status);
          return [relationship.id, {
            source: 'runHistory',
            status: run.status,
            statusLabel: meta.label,
            statusColor: meta.color,
            statusIcon: meta.icon,
            // Status and time must describe the same run, so both come from this run object.
            at: run.completedAt || run.createdAt || null,
            counts: relationshipRunCountEntries(run),
          }];
        }
        // health.lastRunAt outlives its run once retention prunes the history. It proves a run
        // finished but says nothing about the outcome, so no status is claimed here.
        const lastRunAt = relationship?.health?.lastRunAt || null;
        return [relationship.id, lastRunAt
          ? { source: 'health', status: null, statusLabel: '', at: lastRunAt, counts: [] }
          : null];
      }));
    },
  },
  watch: {
    registerIdentity: {
      immediate: true,
      handler(identity, oldIdentity) {
        if (oldIdentity !== undefined && identity !== oldIdentity) this.stopClosedRunPolling();
        this.validationFeedbackById = {};
        this.refreshingStatusRelationshipId = null;
        if (this.register.id) this.load();
      },
    },
    runDialogOpen(isOpen, wasOpen) {
      if (!isOpen && wasOpen) this.continueClosedRun();
    },
    relationshipsNeedingReview(flagged) {
      debugLog('[Relationships] review count derived', {
        needsReview: flagged.length,
        flaggedIds: flagged.map((relationship) => relationship.id),
        items: relationshipAttentionSummary(flagged, this.runHistory).items,
        relationshipsLoaded: this.relationships.length,
        runsLoaded: this.runHistory.length,
        // A non-null cursor means older runs exist that this count has not seen.
        runHistoryComplete: this.runHistoryCursor === null,
        historyAvailability: this.historyAvailability,
      });
    },
    lastRunSummaryById(summaries) {
      debugLog('[Relationships] card last-run state derived', Object.entries(summaries)
        .map(([relationshipId, summary]) => ({
          relationshipId,
          source: summary?.source || 'none',
          status: summary?.status || null,
          at: summary?.at || null,
        })));
    },
  },
  beforeDestroy() {
    this.stopClosedRunPolling();
  },
  methods: {
    async load() {
      this.validationFeedbackById = {};
      return this.$store.dispatch('REGISTERS/relationships/LOAD_WORKSPACE', this.register.id);
    },
    async loadMoreRelationships() {
      try {
        await this.$store.dispatch('REGISTERS/relationships/LOAD_MORE_RELATIONSHIPS');
      } catch (err) {
        // Typed ProblemDetails is rendered by the tab.
      }
    },
    async loadMoreHistory() {
      try {
        await this.$store.dispatch('REGISTERS/relationships/LOAD_MORE_RUN_HISTORY');
      } catch (err) {
        // Typed ProblemDetails is rendered by the tab.
      }
    },
    async retryHistory() {
      try {
        await this.$store.dispatch('REGISTERS/relationships/LOAD_RUN_HISTORY', { reset: true });
      } catch (err) {
        // The run-history card renders its isolated ProblemDetails response.
      }
    },
    async onRunTerminal(run) {
      if (!run?.id || !RELATIONSHIP_RUN_TERMINAL_STATUSES.includes(run.status)) return;
      if (this.terminalRunIds.includes(run.id)) return;
      this.terminalRunIds = [...this.terminalRunIds, run.id];
      this.stopClosedRunPolling();
      this.$emit('run-terminal', run);
      const ownsRefreshState = !this.refreshingStatusRelationshipId;
      if (ownsRefreshState) this.refreshingStatusRelationshipId = run.relationshipId || run.id;
      try {
        await Promise.allSettled([
          this.$store.dispatch('REGISTERS/relationships/REFRESH_RELATIONSHIPS'),
          this.$store.dispatch('REGISTERS/relationships/LOAD_RUN_HISTORY', { reset: true, force: true }),
        ]);
      } finally {
        if (ownsRefreshState) this.refreshingStatusRelationshipId = null;
      }
    },
    stopClosedRunPolling(clearPending = true) {
      this.closedRunPollGeneration += 1;
      if (this.closedRunPoller) window.clearTimeout(this.closedRunPoller);
      this.closedRunPoller = null;
      if (this.closedRunAbortController) this.closedRunAbortController.abort();
      this.closedRunAbortController = null;
      this.closedRunPollInFlight = false;
      if (clearPending) {
        this.closedRunMonitorStartedAt = null;
        this.closedRunMonitorExhausted = false;
        this.closedRunContext = null;
        this.closedRunRefreshPending = false;
      }
    },
    continueClosedRun() {
      const run = this.currentRun;
      if (this.historyMode
        || !run?.id
        || !['queued', 'running'].includes(run.status)) return;

      this.stopClosedRunPolling();
      this.closedRunContext = {
        runId: run.id,
        registerIdentity: this.registerIdentity,
      };
      this.closedRunMonitorStartedAt = Date.now();
      this.closedRunMonitorExhausted = false;
      this.closedRunRefreshPending = true;
      this.$emit('run-refresh-pending', { runId: run.id });
      this.startClosedRunPolling();
    },
    startClosedRunPolling() {
      if (!this.closedRunRefreshPending
        || this.closedRunMonitorExhausted
        || this.closedRunPoller
        || this.closedRunPollInFlight
        || !this.closedRunContext) return;
      const generation = ++this.closedRunPollGeneration;
      this.pollClosedRun(generation);
    },
    closedRunContextIsCurrent(generation) {
      return generation === this.closedRunPollGeneration
        && this.closedRunRefreshPending
        && this.closedRunContext?.registerIdentity === this.registerIdentity;
    },
    async pollClosedRun(generation) {
      if (!this.closedRunContextIsCurrent(generation)) return;
      if (!this.closedRunMonitorStartedAt
        || Date.now() - this.closedRunMonitorStartedAt >= CLOSED_RUN_MONITOR_WINDOW_MS) {
        this.closedRunPoller = null;
        this.closedRunPollInFlight = false;
        this.closedRunMonitorExhausted = true;
        return;
      }

      const remainingMs = CLOSED_RUN_MONITOR_WINDOW_MS
        - (Date.now() - this.closedRunMonitorStartedAt);
      const requestTimeout = Math.max(
        1,
        Math.min(CLOSED_RUN_STATUS_REQUEST_TIMEOUT_MS, remainingMs),
      );
      const controller = new AbortController();
      this.closedRunAbortController = controller;
      this.closedRunPollInFlight = true;
      let run = null;
      try {
        run = await this.$store.dispatch(
          'REGISTERS/relationships/LOAD_RUN_STATUS',
          {
            runId: this.closedRunContext.runId,
            signal: controller.signal,
            timeout: requestTimeout,
          },
        );
      } catch (err) {
        // A later bounded attempt may still observe the authoritative terminal status.
      }

      if (this.closedRunAbortController === controller) this.closedRunAbortController = null;
      if (generation === this.closedRunPollGeneration) this.closedRunPollInFlight = false;
      if (!this.closedRunContextIsCurrent(generation)) return;
      if (run?.id === this.closedRunContext.runId
        && RELATIONSHIP_RUN_TERMINAL_STATUSES.includes(run.status)) {
        await this.onRunTerminal(run);
        return;
      }

      const nextRemainingMs = CLOSED_RUN_MONITOR_WINDOW_MS
        - (Date.now() - this.closedRunMonitorStartedAt);
      if (nextRemainingMs <= 0) {
        this.closedRunMonitorExhausted = true;
        return;
      }
      this.closedRunPoller = window.setTimeout(() => {
        this.closedRunPoller = null;
        this.pollClosedRun(generation);
      }, Math.min(CLOSED_RUN_POLL_INTERVAL_MS, nextRemainingMs));
    },
    retryClosedRunMonitoring() {
      if (!this.closedRunRefreshPending || !this.closedRunContext) return;
      this.closedRunMonitorStartedAt = Date.now();
      this.closedRunMonitorExhausted = false;
      this.startClosedRunPolling();
    },
    async onRunReplaced(run) {
      if (!run?.id) return;

      if (this.closedRunContext && run.id !== this.closedRunContext.runId) {
        const cancelledRunId = this.closedRunContext.runId;
        this.stopClosedRunPolling();
        this.$emit('run-refresh-cancelled', { runId: cancelledRunId });
      }

      // A saved preview is immediately part of run history, even before it reaches a terminal
      // state. Refresh it now so closing the preview exposes it in History without requiring the
      // user to close and reopen the whole register workspace.
      await Promise.allSettled([
        this.$store.dispatch('REGISTERS/relationships/LOAD_RUN_HISTORY', {
          reset: true,
          force: true,
        }),
      ]);
    },
    openCreate() {
      if (!this.canDeclare) return;
      this.editingRelationship = null;
      this.editorOpen = true;
    },
    openEdit(relationship) {
      if (!this.canEditRelationship || relationship?.isEnabled) return;
      this.editingRelationship = relationship;
      this.editorOpen = true;
    },
    async saveRelationship(payload) {
      try {
        if (this.editingRelationship) {
          await this.$store.dispatch('REGISTERS/relationships/UPDATE_RELATIONSHIP', {
            relationship: this.editingRelationship,
            patch: payload,
          });
          this.$dialog.notify.success('Relationship updated. Create a new preview before running it.');
        } else {
          await this.$store.dispatch('REGISTERS/relationships/CREATE_RELATIONSHIP', payload);
          this.$dialog.notify.success('Relationship created and left disabled.');
        }
        this.editorOpen = false;
        this.editingRelationship = null;
      } catch (err) {
        // Keep the editor open with field-level ProblemDetails.
      }
    },
    async validate(relationship) {
      if (!this.canValidateRelationship(relationship) || this.isValidating(relationship)) return;
      const registerId = this.register.id;
      const workspaceRequestId = this.workspaceRequestId;
      const isCurrentWorkspace = () => this.register.id === registerId
        && this.workspaceRequestId === workspaceRequestId;
      this.$delete(this.validationFeedbackById, relationship.id);
      try {
        const result = await this.$store.dispatch(
          'REGISTERS/relationships/VALIDATE_RELATIONSHIP',
          relationship.id,
        );
        if (!isCurrentWorkspace() || result?.stale !== false) return;
        this.$set(this.validationFeedbackById, relationship.id, {
          type: 'success',
          message: 'Relationship settings are valid.',
          code: null,
          traceId: null,
        });
        this.$dialog.notify.success('Relationship validation passed.');
      } catch (err) {
        if (!isCurrentWorkspace()) return;
        this.$set(this.validationFeedbackById, relationship.id, {
          type: 'error',
          message: err.detail || 'Relationship validation failed.',
          code: err.code || null,
          traceId: err.traceId || null,
        });
        this.notifyRefusal(err, 'validate', 'Relationship validation failed.');
      }
    },
    canValidateRelationship(relationship) {
      return this.availability === 'ready'
        && this.access.canEdit === true
        && Boolean(relationship?.id);
    },
    isValidating(relationship) {
      return this.validatingRelationshipIds.includes(relationship?.id);
    },
    validationFeedbackFor(relationship) {
      return this.validationFeedbackById[relationship?.id] || null;
    },
    async toggleEnabled(relationship, { upgradeConfirmed = false } = {}) {
      // Read before the dispatch: the refresh that follows a successful change replaces the listed
      // relationship, leaving this reference on the pre-change copy.
      const enabling = relationship.isEnabled !== true;
      try {
        await this.$store.dispatch('REGISTERS/relationships/SET_RELATIONSHIP_ENABLED', {
          relationship,
          enabled: enabling,
          upgradeConfirmed,
        });
        this.$dialog.notify.success(`Relationship ${enabling ? 'enabled' : 'disabled'}.`);
      } catch (err) {
        // A relationship saved before automatic sync cannot be enabled as it stands, and the
        // upgrade rewrites its saved mapping — so the user is asked rather than told afterwards.
        // Only the unconfirmed attempt asks; the confirmed retry reports its refusal like any other.
        if (enabling && !upgradeConfirmed && err?.code === RELATIONSHIP_AUTOMATIC_CONTRACT_UPGRADE_REQUIRED) {
          this.upgradingRelationshipId = relationship.id;
          this.upgradeDialogOpen = true;
          return;
        }
        this.notifyRefusal(err, enabling ? 'enable' : 'disable', 'The relationship state could not be changed.');
      }
    },
    async confirmAutomaticUpgrade() {
      const relationship = this.upgradingRelationship;
      if (!relationship) {
        this.closeAutomaticUpgrade();
        this.$dialog.notify.error('This relationship is no longer listed. Refresh the workspace, then try again.');
        return;
      }
      // A refresh can land while the question is on screen. Confirming then would send the toggle
      // in whichever direction the list now implies — and this dialog only ever asked to enable.
      if (relationship.isEnabled === true) {
        this.closeAutomaticUpgrade();
        this.$dialog.notify.success('This relationship is already enabled.');
        return;
      }
      try {
        await this.toggleEnabled(relationship, { upgradeConfirmed: true });
      } finally {
        this.closeAutomaticUpgrade();
      }
    },
    closeAutomaticUpgrade() {
      this.upgradeDialogOpen = false;
      this.upgradingRelationshipId = null;
    },
    canRun(relationship) {
      return this.access.canPreview === true
        && relationship.isEnabled === true
        && relationship.attributionStatus !== 'quarantine';
    },
    canSync(relationship) {
      return this.canRun(relationship)
        && Array.isArray(relationship?.mapping)
        && relationship.mapping.some(mapping => mapping?.destination?.kind !== 'link');
    },
    canToggleEnabled(relationship) {
      return this.access.canEnable === true
        && relationship.attributionStatus !== 'quarantine'
        && !this.refreshingStatusRelationshipId;
    },
    canDeleteRelationship(relationship) {
      return this.access.canDelete === true
        && typeof this.access.deleteApprovalPurpose === 'string'
        && Boolean(this.access.deleteApprovalPurpose.trim());
    },
    isRefreshingStatus(relationship) {
      return Boolean(relationship?.id)
        && this.refreshingStatusRelationshipId === relationship.id;
    },
    async refreshRelationshipStatus(relationship) {
      if (
        !relationship?.id
        || this.refreshingStatusRelationshipId
        || this.loading
        || this.loadingRunHistory
      ) return;
      this.refreshingStatusRelationshipId = relationship.id;
      try {
        const results = await Promise.allSettled([
          this.$store.dispatch('REGISTERS/relationships/REFRESH_RELATIONSHIPS'),
          this.$store.dispatch('REGISTERS/relationships/LOAD_RUN_HISTORY', {
            reset: true,
            force: true,
          }),
        ]);
        const failed = results.find(result => result.status === 'rejected');
        if (failed) throw failed.reason;
        this.$dialog.notify.success(`${relationship.name} status refreshed.`);
      } catch (err) {
        const detail = err?.detail || err?.response?.data?.detail;
        this.notifyRefusal(err, 'refresh', detail || 'Relationship status could not be refreshed. Try again.');
      } finally {
        if (this.refreshingStatusRelationshipId === relationship.id) {
          this.refreshingStatusRelationshipId = null;
        }
      }
    },
    // Friendly title, reason, next step and code instead of the raw server sentence
    // (config/relationshipRefusalCopy.js toastCopy). toastHtml escapes every part: the toast
    // renders its text through innerHTML.
    notifyRefusal(err, operation, fallback) {
      const copy = toastCopy(err, operation, this.refusalCopyContext, fallback);
      this.$dialog.notify.error(toastHtml(copy));
    },
    openResolveRun(relationship) {
      if (!this.canRun(relationship)) return;
      this.runningRelationship = relationship;
      this.historyRunId = null;
      this.historyMode = false;
      this.runPreviewMode = RELATIONSHIP_PREVIEW_MODES.RESOLVE;
      this.runDialogOpen = true;
    },
    openSyncRun(relationship) {
      if (!this.canSync(relationship)) return;
      this.runningRelationship = relationship;
      this.historyRunId = null;
      this.historyMode = false;
      this.runPreviewMode = RELATIONSHIP_PREVIEW_MODES.SYNC_FIELDS;
      this.runDialogOpen = true;
    },
    resolvePreviewDescription(relationship) {
      return relationship?.onMiss === 'create'
        ? 'Find existing matches or create missing records'
        : 'Find and connect existing matching records';
    },
    openHistoryRun(run) {
      const runId = run?.id;
      if (!runId) return;
      this.runningRelationship = this.relationships.find((item) => item.id === run.relationshipId) || {
        id: run.relationshipId || '',
        name: 'Historical relationship',
      };
      this.historyRunId = runId;
      this.historyMode = true;
      this.runPreviewMode = RELATIONSHIP_PREVIEW_MODES.RESOLVE;
      this.runDialogOpen = true;
    },
    openDelete(relationship) {
      if (!this.canDeleteRelationship(relationship)) return;
      this.deletingRelationship = relationship;
      this.deleteReviewed = false;
      this.deleteDialogOpen = true;
    },
    async confirmDelete() {
      try {
        await this.$store.dispatch('REGISTERS/relationships/DELETE_RELATIONSHIP', this.deletingRelationship);
        this.$dialog.notify.success('Relationship deleted. Target records were preserved.');
        this.deleteDialogOpen = false;
        this.deletingRelationship = null;
      } catch (err) {
        this.notifyRefusal(err, 'delete', 'The relationship could not be deleted.');
      }
    },
    healthEntries: relationshipHealthEntries,
    coverageLabel: relationshipCoverageLabel,
    lastRunSummaryFor(relationship) {
      return this.lastRunSummaryById[relationship?.id] || null;
    },
    // The workspace loads run history alongside the relationships instead of before them, so until
    // history is ready an unconfirmed preview is invisible here. Claiming "not run yet" in that
    // window would be the same lie the footer used to tell, one load earlier.
    notRunNotice(relationship) {
      if (this.historyAvailability !== 'ready') return '';
      if (this.lastRunSummaryFor(relationship) || this.healthEntries(relationship).length) return '';
      // A disabled relationship cannot run yet; point at the step that unblocks it (AVA-602 U10).
      if (relationship && relationship.isEnabled !== true) {
        return 'Not run yet. Enable it from the ⋮ menu, then choose Connect records.';
      }
      return 'Not run yet. Choose Connect records in the ⋮ menu to see its status.';
    },
    sourceLabel: relationshipSourceLabel,
    targetLabel(relationship) {
      const capability = this.capabilities.find((item) => item.targetKind === relationship?.targetKind);
      return capability?.displayName || relationshipTargetLabel(relationship);
    },
    relationshipProjections(relationship) {
      const mappings = Array.isArray(relationship?.mapping) ? relationship.mapping : [];
      return mappings.map((mapping, index) => {
        const destination = mapping?.destination || {};
        // Keyed lookup rather than a chain so a new destination kind reaches the card by adding one
        // label. The own-property check keeps a stored kind like "toString" from resolving to an
        // inherited member.
        const namespace = Object.prototype.hasOwnProperty.call(
          DESTINATION_NAMESPACE_LABELS,
          destination.kind,
        )
          ? DESTINATION_NAMESPACE_LABELS[destination.kind]
          : 'Unknown namespace';
        return {
          key: `${relationship.id}-mapping-${index}`,
          source: registerColumnLabel(this.registerSchema, mapping.sourceFieldKey),
          destination: destination.labelSnapshot
            || destination.attributeKeySnapshot
            || destination.fieldKey
            || destination.definitionId
            || 'Unknown destination',
          namespace,
        };
      });
    },
    relationshipStatusClass(relationship) {
      if (relationship.attributionStatus === 'quarantine') return 'relationship-card__status-dot--warning';
      if (relationship.state === 'error') return 'relationship-card__status-dot--error';
      if (relationship.isEnabled) return 'relationship-card__status-dot--active';
      return 'relationship-card__status-dot--disabled';
    },
    needsAttention(relationship) {
      return relationshipNeedsAttention(relationship, this.runHistory);
    },
    latestRunFor(relationship) {
      return latestRelationshipRun(this.runHistory, relationship?.id);
    },
    formatDate: formatRelationshipTimestamp,
  },
};
</script>

<style lang="scss" scoped>
.relationships-tab { --relationship-muted: #59635f; --relationship-measure: 1280px; --relationship-skeleton-bone: rgba(41, 49, 46, 0.1); height: 100%; min-width: 0; overflow: auto; color: #202825; background: var(--v-bg1-base); font-family: var(--ds-font-family-body); }
/* Every non-ready state - unavailable, forbidden, error, and the two configuration facts - uses
   this one composition on the same grid as the loaded content, so switching states does not move
   the page. Severity is carried by the icon colour alone; the surface stays neutral. */
.relationship-gate { display: flex; align-items: flex-start; width: 100%; gap: var(--ds-space-md); max-width: var(--relationship-measure); margin: var(--ds-space-lg) auto; padding: var(--ds-space-xl); border: 1px solid var(--ds-border-default); border-radius: var(--ds-radius-lg); background: var(--v-bg2-base); }
.relationship-gate__icon { display: flex; align-items: center; justify-content: center; width: 48px; height: 48px; flex: 0 0 48px; border-radius: 50%; background: var(--v-bg3-base); }
.relationship-gate__icon .v-icon { color: var(--relationship-muted); }
.relationship-gate__body { display: flex; min-width: 0; flex-direction: column; align-items: flex-start; gap: var(--ds-space-xs); }
.relationship-gate h3 { margin: 0; font-family: var(--ds-font-family-heading); font-size: var(--ds-font-subtitle); font-weight: 600; }
.relationship-gate p { max-width: 70ch; margin: 0; color: var(--relationship-muted); font-size: var(--ds-font-body-sm); line-height: 1.55; }
.relationship-gate p.relationship-gate__detail { overflow-wrap: anywhere; font-size: var(--ds-font-caption); }
.relationship-gate .v-btn { margin-top: var(--ds-space-sm); }
.relationship-workspace { width: 100%; height: 100%; min-height: 560px; overflow: hidden; background: var(--v-bg1-base); }
.relationship-workspace__pane { display: flex; width: 100%; height: 100%; min-width: 0; min-height: 0; flex-direction: column; overflow: hidden; background: var(--v-bg1-base); }
.relationship-workspace__pane-header { display: flex; align-items: center; flex: 0 0 auto; min-height: 82px; padding: var(--ds-space-md) var(--ds-space-lg); border-bottom: 1px solid var(--ds-border-subtle); background: var(--v-bg2-base); }
.relationship-workspace__pane-header-inner { display: flex; align-items: center; justify-content: space-between; min-width: 0; flex: 1; gap: var(--ds-space-lg); max-width: var(--relationship-measure); margin: 0 auto; }
.relationship-workspace__pane-title { min-width: 0; }
.relationship-workspace__pane-title h2 { margin: 0; font-family: var(--ds-font-family-heading); font-size: var(--ds-font-title); font-weight: 600; }
.relationship-workspace__pane-title p { max-width: 70ch; margin: 3px 0 0; color: var(--relationship-muted); font-size: var(--ds-font-body-sm); }
.relationship-workspace__pane-actions { display: flex; align-items: center; flex: 0 0 auto; gap: var(--ds-space-xs); }
.relationship-workspace__pane-actions > span:last-child { display: inline-flex; }
.relationship-workspace__loaded-count { color: var(--relationship-muted); font-size: var(--ds-font-caption); font-weight: 600; }
.relationship-workspace__body { display: flex; min-width: 0; min-height: 0; flex: 1; flex-direction: column; overflow: auto; padding: var(--ds-space-lg); }
.relationship-workspace__history { width: 100%; max-width: var(--relationship-measure); margin: 0 auto; border-radius: var(--ds-radius-lg); }
/* The gate states cap at the measure; without this the standalone history panel rendered full
   width beside them, so the two halves of the same failure screen did not line up. */
.relationships-tab__standalone-history { width: 100%; max-width: var(--relationship-measure); margin-right: auto; margin-left: auto; }
.relationship-workspace__history ::v-deep .relationship-history-panel__header { position: absolute; width: 1px; height: 1px; padding: 0; overflow: hidden; border: 0; clip: rect(0 0 0 0); white-space: nowrap; }
.relationship-workspace__history ::v-deep .relationship-history-panel__list { max-height: none; }
.relationship-readonly-notice { display: flex; align-items: flex-start; width: 100%; gap: var(--ds-space-sm); max-width: var(--relationship-measure); margin: 0 auto var(--ds-space-md); padding: var(--ds-space-sm) var(--ds-space-md); border: 1px solid rgba(210, 160, 0, 0.35); border-radius: var(--ds-radius-md); background: rgba(210, 160, 0, 0.08); }
.relationship-readonly-notice > div { display: flex; min-width: 0; flex-direction: column; }
.relationship-readonly-notice span { color: var(--relationship-muted); font-size: var(--ds-font-caption); }
.relationship-run-monitor-notice { align-items: center; }
.relationship-run-monitor-notice > div { flex: 1; }
.relationship-run-monitor-notice .v-btn { flex: 0 0 auto; }
.relationship-overview { display: grid; width: 100%; grid-template-columns: repeat(4, minmax(150px, 1fr)); max-width: var(--relationship-measure); margin: 0 auto var(--ds-space-sm); overflow: hidden; border: 1px solid var(--ds-border-subtle); border-radius: var(--ds-radius-md); background: var(--v-bg2-base); }
.relationship-overview__stat { display: flex; min-width: 0; flex-direction: column; gap: 2px; padding: 12px var(--ds-space-md); border-right: 1px solid var(--ds-border-subtle); }
.relationship-overview__stat:last-child { border-right: 0; }
.relationship-overview__stat span, .relationship-overview__stat small { color: var(--relationship-muted); font-size: var(--ds-font-caption); }
.relationship-overview__stat small { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.relationship-overview__stat strong { font-size: var(--ds-font-title); font-weight: 600; line-height: 1.2; }
.relationship-overview__value--text { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: var(--ds-font-body-sm); line-height: 1.5; }
.relationship-scope-note { display: flex; align-items: center; justify-content: flex-end; width: 100%; gap: var(--ds-space-xs); max-width: var(--relationship-measure); margin: 0 auto var(--ds-space-md); color: var(--relationship-muted); font-size: var(--ds-font-caption); }
.relationship-declarations { width: 100%; max-width: var(--relationship-measure); margin: 0 auto; overflow: hidden; border: 1px solid var(--ds-border-default); border-radius: var(--ds-radius-lg); background: var(--v-bg2-base); }
.relationship-declarations--empty { display: grid; grid-template-columns: minmax(280px, 1fr) minmax(320px, 0.85fr); align-items: stretch; gap: var(--ds-space-xl); padding: var(--ds-space-xl); }
.relationship-empty__intro { display: grid; grid-template-columns: 44px minmax(0, 1fr); align-items: start; gap: var(--ds-space-md); }
.relationship-empty__mark { display: flex; align-items: center; justify-content: center; width: 44px; height: 44px; grid-row: 1 / span 2; border-radius: var(--ds-radius-md); background: var(--ds-surface-active); }
.relationship-empty__intro h3 { margin: 0; font-family: var(--ds-font-family-heading); font-size: var(--ds-font-subtitle); font-weight: 600; }
.relationship-empty__intro p { max-width: 58ch; margin: var(--ds-space-xs) 0 0; color: var(--relationship-muted); font-size: var(--ds-font-body-sm); line-height: 1.55; }
.relationship-empty__intro > .v-btn { grid-column: 2; justify-self: start; margin-top: var(--ds-space-sm); }
.relationship-empty__steps { display: flex; flex-direction: column; gap: 0; margin: 0; padding: 0 0 0 var(--ds-space-xl); border-left: 1px solid var(--ds-border-subtle); list-style: none; }
.relationship-empty__steps li { position: relative; display: grid; grid-template-columns: 30px minmax(0, 1fr); align-items: start; gap: var(--ds-space-sm); min-height: 54px; }
.relationship-empty__steps li:not(:last-child)::after { position: absolute; top: 30px; bottom: 0; left: 14px; width: 1px; background: var(--ds-border-default); content: ''; }
.relationship-empty__steps li > span { display: flex; align-items: center; justify-content: center; width: 30px; height: 30px; z-index: 1; border: 1px solid rgba(3, 98, 76, 0.25); border-radius: 50%; color: var(--v-primary-base); background: var(--v-bg2-base); font-size: var(--ds-font-caption); font-weight: 700; }
.relationship-empty__steps li > div { display: flex; min-width: 0; flex-direction: column; padding-top: 3px; }
.relationship-empty__steps strong { font-size: var(--ds-font-body-sm); }
.relationship-empty__steps small { color: var(--relationship-muted); font-size: var(--ds-font-caption); }
/* The skeleton reuses the loaded containers so the only thing that changes on arrival is the
   content of each bone. design-system.md bans shimmer on loading content, so the bones are
   static: the layout preview is the information, and the header's refresh control carries the
   only motion. */
.relationship-skeleton__overview .relationship-overview__stat:last-child { border-right: 0; }
.relationship-skeleton__stack { display: flex; min-width: 0; flex: 1; flex-direction: column; gap: var(--ds-space-xs); }
.relationship-skeleton__actions { display: flex; align-items: center; flex: 0 0 auto; gap: var(--ds-space-sm); }
/* Mirrors the sentence rail: alternating value tokens and connector words on one wrapping line. */
.relationship-skeleton__rail { display: flex; align-items: center; flex-wrap: wrap; gap: 7px; margin: 0 var(--ds-space-md) 12px; padding: 10px var(--ds-space-md); border: 1px solid var(--ds-border-default); border-radius: var(--ds-radius-md); background: var(--v-bg3-base); }
.relationship-skeleton__bone { display: block; flex: 0 0 auto; border-radius: var(--ds-radius-sm); background: var(--relationship-skeleton-bone); }
.relationship-skeleton__bone--token { width: 132px; height: 22px; }
.relationship-skeleton__bone--word { width: 52px; height: 10px; }
.relationship-skeleton__bone--label { width: 84px; height: 10px; }
.relationship-skeleton__bone--detail { width: 108px; max-width: 90%; height: 10px; }
.relationship-skeleton__bone--value { width: 60%; min-width: 64px; height: 18px; }
.relationship-skeleton__bone--dot { width: 10px; height: 10px; border-radius: 50%; }
.relationship-skeleton__bone--title { width: 190px; max-width: 60%; height: 15px; }
.relationship-skeleton__bone--caption { width: 68px; height: 10px; }
.relationship-skeleton__bone--action { width: 132px; height: 44px; border-radius: var(--ds-radius-md); }
.relationship-skeleton__bone--kebab { width: 28px; height: 28px; border-radius: 50%; }
.relationship-skeleton__bone--icon { width: 32px; height: 32px; }

.relationship-skeleton__bone--chip { width: 108px; height: 20px; border-radius: var(--ds-radius-full); }
.relationship-skeleton__bone--meta { width: 168px; height: 20px; }
.relationship-card { overflow: hidden; border: 0; border-bottom: 1px solid var(--ds-border-subtle); border-radius: 0; background: var(--v-bg2-base); }
.relationship-card:last-of-type { border-bottom: 0; }
.relationship-card--quarantine { background: rgba(210, 160, 0, 0.045); }
.relationship-card__header, .relationship-card__footer { display: flex; align-items: center; justify-content: space-between; gap: var(--ds-space-md); padding: 12px var(--ds-space-md); }
.relationship-card__header { border-bottom: 1px solid var(--ds-border-subtle); }
.relationship-card__identity { display: flex; align-items: center; min-width: 0; gap: var(--ds-space-sm); }
.relationship-card__identity h3 { margin: 0; font-size: var(--ds-font-subtitle); }
.relationship-card__identity > div:last-child { min-width: 0; }
.relationship-card__identity > div:last-child > span { color: var(--relationship-muted); font-size: var(--ds-font-caption); }
.relationship-card__actions { display: flex; align-items: center; flex: 0 0 auto; }
.relationship-card__actions > span { display: inline-flex; }
.relationship-card__status-dot { width: 10px; height: 10px; flex: 0 0 10px; border-radius: 50%; background: var(--v-grey-base); }
.relationship-card__status-dot--active { background: var(--v-success-base); }
.relationship-card__status-dot--warning { background: var(--v-warning-base); }
.relationship-card__status-dot--error { background: var(--v-error-base); }
/* The sentence rail is a self-contained bordered object, so the card insets it rather than
   stripping its edges as the old edge-to-edge three-column rail required. */
.relationship-card__rail { margin: var(--ds-space-md) 12px 0; }
.relationship-card__footer { color: var(--relationship-muted); }
.relationship-card__health { display: flex; align-items: center; flex-wrap: wrap; min-width: 0; gap: var(--ds-space-xs); }
.relationship-card__run-status { display: inline-flex; align-items: center; gap: var(--ds-space-xs); margin-right: var(--ds-space-xs); color: #202825; font-size: var(--ds-font-body-sm); font-weight: 600; }
.relationship-card__health-meta { display: flex; align-items: flex-end; flex: 0 0 auto; flex-direction: column; gap: 2px; color: var(--relationship-muted); text-align: right; font-size: var(--ds-font-caption); }
.relationship-card__validation { display: flex; align-items: flex-start; gap: var(--ds-space-sm); padding: var(--ds-space-sm) var(--ds-space-md); border-top: 1px solid var(--ds-border-subtle); background: rgba(35, 134, 54, 0.08); }
.relationship-card__recovery { display: flex; align-items: center; gap: var(--ds-space-sm); padding: var(--ds-space-sm) var(--ds-space-md); border-top: 1px solid rgba(210, 160, 0, 0.3); background: rgba(210, 160, 0, 0.08); }
.relationship-card__recovery > div { display: flex; min-width: 0; flex: 1; flex-direction: column; }
.relationship-card__recovery strong { font-size: var(--ds-font-body-sm); }
.relationship-card__recovery span { color: var(--relationship-muted); font-size: var(--ds-font-caption); }
.relationship-card__validation--loading { background: var(--ds-surface-active); }
.relationship-card__validation--error { background: rgba(183, 28, 28, 0.08); }
.relationship-card__validation > div { display: flex; min-width: 0; flex-direction: column; }
.relationship-card__validation strong { font-size: var(--ds-font-body-sm); }
.relationship-card__validation span { color: var(--relationship-muted); overflow-wrap: anywhere; font-size: var(--ds-font-caption); }
.relationship-history-empty { display: flex; align-items: center; width: 100%; gap: var(--ds-space-md); max-width: var(--relationship-measure); margin: 0 auto; padding: var(--ds-space-xl); border: 1px solid var(--ds-border-default); border-radius: var(--ds-radius-lg); background: var(--v-bg2-base); }
.relationship-history-empty__mark { display: flex; align-items: center; justify-content: center; width: 48px; height: 48px; flex: 0 0 48px; border-radius: var(--ds-radius-md); background: var(--ds-surface-active); }
.relationship-history-empty h3 { margin: 0; font-family: var(--ds-font-family-heading); font-size: var(--ds-font-subtitle); font-weight: 600; }
.relationship-history-empty p { margin: 3px 0 0; color: var(--relationship-muted); font-size: var(--ds-font-body-sm); }
.relationship-declarations__load-more { border-top: 1px solid var(--ds-border-subtle); border-radius: 0; }
@media (max-width: 1100px) {
  .relationship-workspace__pane-header { align-items: flex-start; }
  .relationship-workspace__pane-header-inner { align-items: flex-start; flex-wrap: wrap; }
  .relationship-workspace__body { padding: var(--ds-space-md); }
  .relationship-overview { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .relationship-overview__stat:nth-child(2n) { border-right: 0; }
  .relationship-overview__stat:nth-child(n + 3) { border-top: 1px solid var(--ds-border-subtle); }
  .relationship-declarations--empty { grid-template-columns: minmax(0, 1fr); gap: var(--ds-space-lg); padding: var(--ds-space-lg); }
  .relationship-empty__intro, .relationship-empty__steps { width: 100%; }
  .relationship-empty__steps { padding: var(--ds-space-lg) 0 0; border-top: 1px solid var(--ds-border-subtle); border-left: 0; }
}

.relationships-tab ::v-deep .v-btn { min-height: 44px; }
.relationships-tab ::v-deep .v-btn--icon { width: 44px; min-width: 44px; height: 44px; }
.relationships-tab ::v-deep .v-btn.v-btn--disabled:not(.v-btn--text):not(.v-btn--icon) { color: #626b68 !important; background-color: #dfe4e2 !important; opacity: 1; }
.relationships-tab ::v-deep .v-btn.v-btn--disabled:not(.v-btn--text):not(.v-btn--icon) .v-icon { color: #626b68 !important; }

@media (prefers-reduced-motion: reduce) {
  .relationships-tab *, .relationships-tab *::before, .relationships-tab *::after { transition-duration: 0.01ms !important; animation-duration: 0.01ms !important; }
}
</style>

<!-- Unscoped: v-menu teleports its content to the [data-app] root, so it is not a descendant of
     this component and carries no scoped data-v attribute. Reach is guaranteed by the unique
     content-class instead; the same pattern the repo already uses for menu/dialog content. -->
<style lang="scss">
/* Menu content is teleported to the .v-application root and carries no scoped data-v attribute,
   so it is styled by the unique content-class. The leading .v-application raises every selector to
   (0,4,0) — this is the load-bearing part: without it, `.v-menu__content.relationship-card-menu
   .v-list-item__icon` (0,3,0) TIES Vuetify's own `.v-application--is-ltr .v-list-item__icon
   :first-child` margin rule (also 0,3,0) and loses on source order, so the 32px default gap never
   moved through three earlier passes. .v-application is on the root in both LTR and RTL, so the
   bump is direction-agnostic. */
.v-application .v-menu__content.relationship-card-menu {
  .v-list-item {
    min-height: 44px;
    padding-top: 6px;
    padding-bottom: 6px;
  }

  .v-list-item__content { padding: 0; }

  /* center the glyph against the row so single-line items read level with their label; on the two
     Vuetify swaps an 18px progress spinner in (Refresh status, Validate), so the box is fixed at
     18px to keep the label from shifting sideways when one spins. */
  .v-list-item__icon {
    align-self: center;
    min-width: 18px;
    margin: 0 12px 0 0;
  }

  .v-list-item__subtitle {
    margin-top: 2px;
    color: #59635f;
    white-space: normal;
    font-size: var(--ds-font-caption);
    line-height: 1.4;
  }
}
</style>
